import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  CalendarDays, Activity, Users, Wallet, Settings, CreditCard,
  ScanLine, Target, BarChart3, LogOut, Menu, X, Bell, Coins,
  CheckCheck, Compass, TrendingUp, Sun, Moon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { notificationsApi, eventsApi } from '../api/httpClient';
import OnboardingTour from './OnboardingTour';

/** V2 Layout — no framer-motion */

// ── Nav config ────────────────────────────────────────────────────────────────
const NAV = {
  platform_admin: [
    { to: '/dashboard',         Icon: BarChart3,    label: 'Dashboard'     },
    { to: '/events',            Icon: CalendarDays, label: 'All Events'    },
    { to: '/team',              Icon: Users,        label: 'All Users'     },
    { to: '/admin/pricing',     Icon: CreditCard,   label: 'Token Pricing' },
    { to: '/platform/revenue',  Icon: TrendingUp,   label: 'Revenue'       },
    { to: '/platform/settings', Icon: Settings,     label: 'Platform Setup'},
    { to: '/discover',          Icon: Compass,      label: 'Discover'      },
  ],
  org_admin: [
    { to: '/events',       id: 'nav-events',   Icon: CalendarDays, label: 'Events'       },
    { to: '/team',         id: 'nav-team',     Icon: Users,        label: 'Team'         },
    { to: '/wallet',       id: 'nav-wallet',   Icon: Wallet,       label: 'Token Wallet' },
    { to: '/org/settings', id: 'nav-settings', Icon: Settings,     label: 'Settings'     },
    { to: '/discover',     id: 'nav-discover', Icon: Compass,      label: 'Discover'     },
  ],
  event_admin: [
    { to: '/events',   Icon: CalendarDays, label: 'My Events' },
    { to: '/team',     Icon: Users,        label: 'Team'      },
    { to: '/discover', Icon: Compass,      label: 'Discover'  },
  ],
  volunteer: [
    { to: '/scan',     Icon: ScanLine, label: 'Scanner'  },
    { to: '/discover', Icon: Compass,  label: 'Discover' },
  ],
  sponsor: [
    { to: '/sponsor',  Icon: Target,  label: 'Lead Capture' },
    { to: '/discover', Icon: Compass, label: 'Discover'     },
  ],
};

const SIDEBAR_W = 252;

// ── Logo mark ─────────────────────────────────────────────────────────────────
function LogoMark({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
      <rect width="36" height="36" rx="10" fill="url(#lgm2)" />
      <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle"
        fill="#fff" fontSize="15" fontWeight="800"
        fontFamily="'Plus Jakarta Sans', sans-serif">CG</text>
      <defs>
        <linearGradient id="lgm2" x1="0" y1="0" x2="36" y2="36" gradientUnits="userSpaceOnUse">
          <stop stopColor="#7B2FE4" />
          <stop offset=".52" stopColor="#C71D8E" />
          <stop offset="1" stopColor="#F5176B" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// ── Avatar initials ───────────────────────────────────────────────────────────
function AvatarInitials({ name, size = 36 }) {
  const initials = (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg,#7B2FE4,#F5176B)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.35), fontWeight: 800, color: '#fff',
    }}>
      {initials}
    </div>
  );
}

// ── Theme toggle ──────────────────────────────────────────────────────────────
function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <div className="theme-toggle">
      <button
        className={theme === 'day' ? 'on' : ''}
        onClick={() => theme !== 'day' && toggleTheme()}
        title="Light"
        aria-label="Light mode"
      >
        <Sun size={16} />
      </button>
      <button
        className={theme === 'night' ? 'on' : ''}
        onClick={() => theme !== 'night' && toggleTheme()}
        title="Dark"
        aria-label="Dark mode"
      >
        <Moon size={16} />
      </button>
    </div>
  );
}

// ── Notification bell ─────────────────────────────────────────────────────────
function NotificationBell() {
  const [open,    setOpen]    = useState(false);
  const [items,   setItems]   = useState([]);
  const [unread,  setUnread]  = useState(0);
  const [loading, setLoading] = useState(false);
  const dropRef = useRef(null);

  const fetchNotifs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const d = await notificationsApi.list();
      setItems(d.notifications || []);
      setUnread(d.unread_count  || 0);
    } catch {}
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(() => fetchNotifs(true), 60_000);
    return () => clearInterval(t);
  }, [fetchNotifs]);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const handleMarkRead = async (id) => {
    setItems(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    setUnread(u => Math.max(0, u - 1));
    await notificationsApi.markRead(id).catch(() => {});
  };

  const handleMarkAllRead = async () => {
    setItems(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    setUnread(0);
    await notificationsApi.markAllRead().catch(() => {});
  };

  const timeAgo = (dt) => {
    const s = Math.floor((Date.now() - new Date(dt)) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return Math.floor(s / 60) + 'm ago';
    if (s < 86400) return Math.floor(s / 3600) + 'h ago';
    return Math.floor(s / 86400) + 'd ago';
  };

  return (
    <div ref={dropRef} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(o => !o); if (!open) fetchNotifs(); }}
        style={{
          position: 'relative',
          background: open ? 'var(--primary-50)' : 'var(--surface-2)',
          border: open ? '1.5px solid var(--primary-100)' : '1.5px solid var(--line)',
          borderRadius: 'var(--r-sm)', padding: '9px',
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          color: open ? 'var(--primary)' : 'var(--ink-2)',
          transition: 'all .15s var(--ease)',
        }}
      >
        <Bell size={18} strokeWidth={1.75} />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -5, right: -5,
            background: 'var(--primary)', color: '#fff',
            fontSize: 10, fontWeight: 800, borderRadius: 99,
            minWidth: 17, height: 17, display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: '0 4px',
            border: '2px solid var(--bg)', lineHeight: 1,
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          width: 360, maxHeight: 480,
          background: 'var(--surface)', borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--sh-lg)', border: '1px solid var(--line)',
          overflow: 'hidden', zIndex: 200,
          display: 'flex', flexDirection: 'column',
          animation: 'dropIn 0.14s ease-out',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px 10px', borderBottom: '1px solid var(--line)', flexShrink: 0,
          }}>
            <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--ink)' }}>
              Notifications
              {unread > 0 && (
                <span style={{
                  marginLeft: 6, background: 'var(--primary)', color: '#fff',
                  fontSize: 11, fontWeight: 700, borderRadius: 99, padding: '1px 7px',
                }}>{unread}</span>
              )}
            </span>
            {unread > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: 'var(--primary)',
                  padding: '4px 8px', borderRadius: 6, fontFamily: 'inherit',
                  transition: 'background .12s',
                }}
              >
                <CheckCheck size={13} strokeWidth={2} /> Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
            ) : items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
                <div style={{ fontSize: 13 }}>Nothing here yet</div>
              </div>
            ) : items.map(n => (
              <div
                key={n.id}
                onClick={() => {
                  if (!n.read_at) handleMarkRead(n.id);
                  if (n.action_url) { window.location.href = n.action_url; setOpen(false); }
                }}
                style={{
                  padding: '12px 16px', borderBottom: '1px solid var(--line)',
                  cursor: n.action_url ? 'pointer' : 'default',
                  background: n.read_at ? 'transparent' : 'rgba(200,30,120,0.04)',
                  transition: 'background .12s', display: 'flex', gap: 10, alignItems: 'flex-start',
                }}
                onMouseEnter={e => e.currentTarget.style.background = n.read_at ? 'var(--bg)' : 'rgba(200,30,120,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = n.read_at ? 'transparent' : 'rgba(200,30,120,0.04)'}
              >
                <div style={{
                  width: 7, height: 7, borderRadius: '50%', marginTop: 5, flexShrink: 0,
                  background: n.read_at ? 'transparent' : 'var(--primary)',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)', lineHeight: 1.4 }}>{n.title}</div>
                  {n.body && (
                    <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {n.body}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{timeAgo(n.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`@keyframes dropIn { from { opacity:0; transform:translateY(6px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>
    </div>
  );
}

// ── Header search (events) ────────────────────────────────────────────────────
function HeaderSearch() {
  const navigate = useNavigate();
  const [q, setQ]         = useState('');
  const [events, setEvents] = useState(null); // null = not yet loaded
  const [open, setOpen]   = useState(false);
  const boxRef = useRef(null);

  const ensureLoaded = useCallback(async () => {
    if (events !== null) return;
    try { const d = await eventsApi.list(); setEvents(d.events || []); }
    catch { setEvents([]); }
  }, [events]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const term = q.trim().toLowerCase();
  const results = term
    ? (events || []).filter(ev => (ev.title || ev.name || '').toLowerCase().includes(term)).slice(0, 8)
    : [];

  const go = (ev) => { setOpen(false); setQ(''); navigate(`/events/${ev.id}`); };

  return (
    <div className="search" ref={boxRef} style={{ position: 'relative' }}>
      <svg width="16" height="16" fill="none" stroke="var(--ink-3)" strokeWidth="2"
        viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
      </svg>
      <input
        placeholder="Search events…"
        value={q}
        onFocus={() => { ensureLoaded(); setOpen(true); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && results[0]) go(results[0]); if (e.key === 'Escape') setOpen(false); }}
      />
      {open && term && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, zIndex: 200,
          background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
          boxShadow: 'var(--sh-lg)', overflow: 'hidden', maxHeight: 340, overflowY: 'auto',
        }}>
          {events === null ? (
            <div style={{ padding: 14, fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: 'var(--ink-3)' }}>No events match “{q.trim()}”</div>
          ) : results.map(ev => (
            <button key={ev.id} onClick={() => go(ev)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                padding: '10px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--line)',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, color: 'var(--ink)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <CalendarDays size={15} strokeWidth={1.75} style={{ flexShrink: 0, color: 'var(--ink-3)' }} />
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {ev.title || ev.name || 'Untitled event'}
              </span>
              {ev.status && (
                <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                  color: 'var(--ink-3)', flexShrink: 0 }}>{ev.status}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sidebar content ───────────────────────────────────────────────────────────
function SidebarContent({ user, navItems, onClose, handleLogout }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '18px 14px' }}>

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 10 }}>
        <LogoMark size={36} />
        <div>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 17, lineHeight: 1 }}>Click n Go</div>
          <div style={{ color: 'rgba(255,255,255,0.38)', fontSize: 11, marginTop: 2 }}>Event Platform</div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '4px 0' }}>
        {navItems.map(item => (
          <NavLink
            key={item.to}
            id={item.id}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
          >
            <item.Icon size={20} strokeWidth={1.75} style={{ flexShrink: 0 }} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="side-foot">
        <Link to="/profile" onClick={onClose}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 9,
            background: 'rgba(255,255,255,0.05)', borderRadius: 'var(--r-md)',
            border: '1px solid rgba(255,255,255,0.10)', marginBottom: 8, textDecoration: 'none' }}
        >
          <AvatarInitials name={user?.name} size={30} />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.name}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.38)', fontSize: 10, marginTop: 1, textTransform: 'capitalize' }}>
              {user?.role?.replace(/_/g, ' ')} · Edit profile
            </div>
          </div>
        </Link>
        <button
          onClick={handleLogout}
          style={{
            width: '100%', padding: '9px 12px',
            background: 'transparent', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--r-md)', color: 'rgba(255,255,255,0.48)',
            fontSize: 13, cursor: 'pointer', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 8,
            transition: 'all .18s', fontFamily: 'inherit',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(226,49,69,0.14)'; e.currentTarget.style.color = '#F9A8A8'; e.currentTarget.style.borderColor = 'rgba(226,49,69,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.48)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)'; }}
        >
          <LogOut size={16} strokeWidth={1.75} />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────
export default function Layout({ children }) {
  const { user, logout }   = useAuth();
  const navigate           = useNavigate();
  const location           = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile]     = useState(window.innerWidth <= 1024);
  const [showTour, setShowTour]     = useState(
    user?.role === 'org_admin' && user?.onboarding_completed === false
  );

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const navItems     = NAV[user?.role] || [];
  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ── Desktop sidebar ──────────────────────────────────────────── */}
      {!isMobile && (
        <aside style={{
          width: SIDEBAR_W, flexShrink: 0, position: 'sticky', top: 0, height: '100vh',
          background: 'linear-gradient(186deg, #220F3A 0%, #160A22 100%)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          overflowY: 'auto', overflowX: 'hidden',
        }}>
          <SidebarContent user={user} navItems={navItems} onClose={null} handleLogout={handleLogout} />
        </aside>
      )}

      {/* ── Mobile drawer + scrim (CSS transition, no framer-motion) ── */}
      {isMobile && (
        <>
          {/* Scrim */}
          <div
            className={`nav-scrim${drawerOpen ? ' visible' : ''}`}
            onClick={() => setDrawerOpen(false)}
          />

          {/* Drawer */}
          <aside
            className={`sidebar${drawerOpen ? ' cng-open' : ''}`}
            style={{ width: SIDEBAR_W, overflowY: 'auto' }}
          >
            <SidebarContent user={user} navItems={navItems} onClose={() => setDrawerOpen(false)} handleLogout={handleLogout} />
          </aside>
        </>
      )}

      {/* ── Main ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>

        {/* Topbar */}
        <header className="topbar" style={{ zIndex: 50 }}>
          {/* Hamburger (mobile) */}
          {isMobile && (
            <button
              className="nav-burger"
              onClick={() => setDrawerOpen(d => !d)}
              aria-label="Open menu"
            >
              {drawerOpen ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
            </button>
          )}

          {/* Logo (mobile) */}
          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LogoMark size={26} />
              <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--ink)' }}>Click n Go</span>
            </div>
          )}

          {/* Search (desktop) */}
          {!isMobile && <HeaderSearch />}

          {/* Right side */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {user?.role === 'org_admin' && (
              <Link to="/wallet" id="topbar-token-balance" className="tokens-badge" style={{ textDecoration: 'none' }}>
                <Coins size={14} strokeWidth={2} />
                <span>{(user?.token_balance ?? 0).toLocaleString()} tokens</span>
              </Link>
            )}

            <ThemeToggle />
            <div id="topbar-bell"><NotificationBell /></div>

            <Link to="/profile" style={{ textDecoration: 'none' }}>
              <AvatarInitials name={user?.name} size={36} />
            </Link>
          </div>
        </header>

        {/* Page content */}
        <div style={{ flex: 1 }}>
          {children}
        </div>
      </div>

      {/* Onboarding tour */}
      {showTour && <OnboardingTour onDone={() => setShowTour(false)} />}
    </div>
  );
}
